import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CdkDragEnd, Point } from '@angular/cdk/drag-drop';
import {
  QUICK_ACCESS_FLOATING_BOTTOM_MARGIN,
  QUICK_ACCESS_FLOATING_DEFAULT_OFFSET_Y,
  QUICK_ACCESS_FLOATING_TOP_MARGIN,
} from 'src/app/core/helpers/ui/quick-access.constant';
import {
  QuickAccessFloatingSide,
  QuickAccessPage,
} from 'src/app/core/interfaces/ui/quick-access.interface';

@Component({
  selector: 'shared-floating-quick-access',
  templateUrl: './floating-quick-access.component.html',
  styleUrls: ['./floating-quick-access.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FloatingQuickAccessComponent implements OnInit {
  @Input() showHint: boolean = false;
  @Input() suppressNavigation: boolean = false;
  @Input() initialPositionY: number | null = null;
  @Input() initialSide: QuickAccessFloatingSide = 'right';
  @Input() slots: (QuickAccessPage | null)[] = [];

  @Output() customize = new EventEmitter<void>();
  @Output() dismissHint = new EventEmitter<void>();
  @Output() positionChanged = new EventEmitter<number>();
  @Output() sideChanged = new EventEmitter<QuickAccessFloatingSide>();
  @Output() actionClick = new EventEmitter<QuickAccessPage>();

  public readonly topMargin: number = QUICK_ACCESS_FLOATING_TOP_MARGIN;
  public readonly bottomMargin: number = QUICK_ACCESS_FLOATING_BOTTOM_MARGIN;

  public dragPosition: Point = { x: 0, y: QUICK_ACCESS_FLOATING_DEFAULT_OFFSET_Y };
  public side: QuickAccessFloatingSide = 'right';

  public expanded: boolean = false;

  ngOnInit(): void {
    const y = this.initialPositionY ?? QUICK_ACCESS_FLOATING_DEFAULT_OFFSET_Y;
    this.dragPosition = { x: 0, y: this.clampY(y) };
    this.side = this.initialSide;
  }

  get filledSlots(): QuickAccessPage[] {
    return this.slots.filter((slot): slot is QuickAccessPage => !!slot);
  }

  public toggle(): void {
    if (this.suppressNavigation) return;
    this.expanded = !this.expanded;
  }

  public collapse(): void {
    this.expanded = false;
  }

  public onItemClick(slot: QuickAccessPage): void {
    this.collapse();
    if (slot.isAction) this.actionClick.emit(slot);
  }

  public onItemLongPress(): void {
    this.collapse();
    this.customize.emit();
  }

  public onDragEnded(event: CdkDragEnd): void {
    const rect = event.source.element.nativeElement.getBoundingClientRect();
    const droppedCenterX = rect.left + rect.width / 2;
    const side: QuickAccessFloatingSide = droppedCenterX < window.innerWidth / 2 ? 'left' : 'right';

    const y = this.clampY(event.source.getFreeDragPosition().y);
    this.dragPosition = { x: 0, y };

    if (side !== this.side) {
      this.side = side;
      this.sideChanged.emit(side);
    }

    this.positionChanged.emit(y);
  }

  private clampY(y: number): number {
    const half = window.innerHeight / 2;
    const min = this.topMargin - half;
    const max = half - this.bottomMargin;
    return Math.min(max, Math.max(min, y));
  }
}
